<template>
  <q-btn
    :loading="isLoading"
    :color="color"
    @click="select"
  >
    {{ label }} ({{ total }})
  </q-btn>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import _ from 'lodash';
import { OpenDateRange, PostFilters } from '@/type';
import { HasTaskMixin } from '@/mixins';
import {
  isPostLoss,
  getPreLossRange,
  getPostLossRange,
  isPreLossRange,
  isPostLossRange,
} from '@/client/client';
import eventBus from '@/messaging/event-bus';
import {
  PostEvent,
  PostSaved,
  PostEscalated,
  PostResolved,
  PostTrashed,
  PostRestored,
  PostMovedToTimeline,
  PostRemovedFromTimeline,
} from '@/messaging/events';
import postRepository from '../../post-repository';
import { matches } from '../../post';

interface Data {
  task: Promise<number> | null,
  total: number,
}

export default defineComponent({
  emits: ['update:modelValue'],
  mixins: [HasTaskMixin],
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    dateOfLoss: {
      type: String,
      required: true,
    },
    modelValue: Object as () => OpenDateRange,
    filters: {
      type: Object as () => PostFilters,
      required: true,
    },
    postDateOfLoss: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      task: null,
      total: 0,
    };
  },
  computed: {
    label(): string {
      return this.postDateOfLoss ? 'Post Loss' : 'Pre Loss';
    },
    dateRange(): OpenDateRange {
      if (this.postDateOfLoss) return getPostLossRange(this.dateOfLoss);
      return getPreLossRange(this.dateOfLoss);
    },
    actualFilters(): PostFilters {
      return {
        ...this.filters,
        clientIds: [this.clientId],
        since: this.dateRange.start,
        until: this.dateRange.end,
      };
    },
    isSelected(): boolean {
      if (!this.modelValue) return false;
      if (this.postDateOfLoss && isPostLossRange(this.dateOfLoss, this.modelValue)) return true;
      return (!this.postDateOfLoss && isPreLossRange(this.dateOfLoss, this.modelValue));
    },
    color(): string | undefined {
      return this.isSelected ? 'primary' : undefined;
    },
  },
  watch: {
    actualFilters(newStatuses: number[], oldStatuses: number[]) {
      if (!_.isEqual(newStatuses, oldStatuses)) this.count();
    },
  },
  methods: {
    count() {
      this.task = postRepository.count(this.actualFilters);
      this.task.then((total) => {
        this.total = total;
      });
    },
    select() {
      this.$emit('update:modelValue', this.dateRange);
    },
    onPostChanged(event: PostEvent) {
      const { post } = event;
      // Posts don't carry their label/person tags, so membership in a
      // label/person-filtered set can't be decided locally.
      if ((this.filters.labelIds || []).length
        || (this.filters.labelCategoryIds || []).length
        || (this.filters.personIds || []).length) {
        this.count();
        return;
      }
      const postLoss = isPostLoss(this.dateOfLoss, post.postedAt);
      const match = matches(post, this.filters);
      if (
        this.total > 0 && ((this.postDateOfLoss && postLoss && !match)
          || (!this.postDateOfLoss && !postLoss && !match))) {
        this.total -= 1;
        return;
      }
      if (
        (this.postDateOfLoss && postLoss && match)
        || (!this.postDateOfLoss && !postLoss && match)) {
        this.total += 1;
      }
    },
  },
  created() {
    this.count();
    eventBus.$on(PostSaved.eventName, this.onPostChanged);
    eventBus.$on(PostEscalated.eventName, this.onPostChanged);
    eventBus.$on(PostResolved.eventName, this.onPostChanged);
    eventBus.$on(PostTrashed.eventName, this.onPostChanged);
    eventBus.$on(PostRestored.eventName, this.onPostChanged);
    eventBus.$on(PostMovedToTimeline.eventName, this.onPostChanged);
    eventBus.$on(PostRemovedFromTimeline.eventName, this.onPostChanged);
  },
  beforeUnmount(): void {
    eventBus.$off(PostSaved.eventName, this.onPostChanged);
    eventBus.$off(PostEscalated.eventName, this.onPostChanged);
    eventBus.$off(PostResolved.eventName, this.onPostChanged);
    eventBus.$off(PostTrashed.eventName, this.onPostChanged);
    eventBus.$off(PostRestored.eventName, this.onPostChanged);
    eventBus.$off(PostMovedToTimeline.eventName, this.onPostChanged);
    eventBus.$off(PostRemovedFromTimeline.eventName, this.onPostChanged);
  },
});

</script>
